import { Timestamp, UserId } from "@pax/api-contract/common";
import {
  BasemapDraftDocument,
  BasemapName,
  RoomBase,
  RoomOverlay,
  RoomPlace,
  Sha256,
} from "@pax/basemap";
import { z } from "zod";

const RoomId = z.uuid().meta({ description: "The basemap draft's version id." });
export const RoomIdParams = z.strictObject({ id: RoomId });

/** How many commits a room has taken, counting from its beginning. */
export const RoomSeq = z.int().nonnegative();

/** A changed tile, by its raster and place, and the blob holding its pixels. */
const RoomTile = z.strictObject({ raster: BasemapName, place: RoomPlace, blob: Sha256 });

/** Everything a draft is, at one commit. */
export const RoomSnapshot = z
  .strictObject({
    seq: RoomSeq,
    document: BasemapDraftDocument,
    base: RoomBase.meta({
      description:
        "Each raster's and feature set's base, whose tiles fill every place nobody changed. Empty for a new world.",
    }),
    splits: z.record(BasemapName, z.array(RoomPlace)),
    tiles: z.array(RoomTile),
    features: z.record(BasemapName, Sha256).meta({
      description:
        "Each feature set someone brought features into, and the blob holding them whole. Every other set is its base's.",
    }),
    overlays: z.array(RoomOverlay),
    published: z.boolean().meta({
      description: "Whether the draft was published, which closes its room for good.",
    }),
  })
  .meta({ id: "BasemapRoomSnapshot" });
export type RoomSnapshot = z.infer<typeof RoomSnapshot>;

export const BeginRoomRequest = z
  .strictObject({
    document: BasemapDraftDocument,
    base: RoomBase.default({ rasters: {}, featureSets: {} }),
    splits: z.record(BasemapName, z.array(RoomPlace)).default({}),
  })
  .meta({ id: "BeginBasemapRoomRequest" });

export const FreezeRoomRequest = z.strictObject({
  publishId: z.string().min(1).max(200),
});

export const ThawRoomRequest = z.union([
  z.strictObject({ problem: z.string().max(2000) }),
  z.strictObject({
    published: z.strictObject({
      versionId: z.uuid().meta({ description: "The version the draft became." }),
    }),
  }),
]);

export const RoomConnection = z
  .strictObject({
    token: z.string().min(1).meta({ description: "Send it in the first socket frame." }),
    expiresAt: Timestamp,
    userId: UserId,
  })
  .meta({ id: "BasemapRoomConnection" });
